/**
 * Public server function: fetch marketplace-visible coaches, ranked with a strong
 * boost for founding and featured coaches so they dominate results.
 *
 * Used in the intake output step and (via fallback) the dashboard. No authentication
 * required — safe to call from unauthenticated contexts.
 */

import { createServerFn } from "@tanstack/react-start";
import { and, eq, isNotNull, ne } from "drizzle-orm";
import { z } from "zod";

import { MembershipPlan } from "~/lib/schemas/db-enums";

export const FeaturedCoachSchema = z.object({
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  coachingTitle: z.string().nullable(),
  profilePhotoUrl: z.string().nullable(),
  isFoundingCoach: z.boolean(),
  isFeaturedCoach: z.boolean(),
  membershipPlan: z.nativeEnum(MembershipPlan),
});

export type FeaturedCoach = z.infer<typeof FeaturedCoachSchema>;

const featuredCoachesInputSchema = z.object({
  limit: z.number().int().min(1).max(20).default(8),
  /** Optional intake pressureDomain value — used for relevance weighting */
  pressureDomain: z.string().optional(),
});

/** Map intake pressure domains to coaching category keywords for relevance scoring */
const DOMAIN_KEYWORDS: Record<string, string[]> = {
  career_business: ["career", "business", "leadership", "executive"],
  health_energy: ["health", "wellness", "energy", "fitness"],
  relationships: ["relationship", "love", "family", "communication"],
  identity_direction: ["life", "purpose", "mindset", "identity"],
  money_security: ["financial", "money", "wealth", "business"],
};

export const getPublicFeaturedCoaches = createServerFn({ method: "GET" })
  .validator(featuredCoachesInputSchema)
  .handler(async ({ data }): Promise<FeaturedCoach[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coaches, profiles, coachDetails } =
      await import("../../../drizzle/schema");

    const db = await getDBWithInit();

    const rows = await db
      .select({
        username: coaches.username,
        firstName: coaches.firstName,
        lastName: coaches.lastName,
        profilePhotoUrl: coaches.profilePictureUrl,
        isFoundingCoach: coaches.isFoundingCoach,
        isFeaturedCoach: coaches.isFeaturedCoach,
        membershipPlan: coaches.membershipPlan,
        coachingTitle: profiles.coachingTitle,
        mainCoachingCategory: coachDetails.mainCoachingCategory,
      })
      .from(coaches)
      .innerJoin(profiles, eq(profiles.coachId, coaches.id))
      .leftJoin(coachDetails, eq(coachDetails.coachId, coaches.id))
      .where(
        and(
          eq(profiles.isPublic, true),
          isNotNull(coaches.marketplaceApprovedAt),
          isNotNull(coaches.profilePictureUrl),
          ne(coaches.profilePictureUrl, ""),
        ),
      )
      .limit(50); // fetch a pool larger than needed so we can rank + slice

    const domainKeywords = data.pressureDomain
      ? (DOMAIN_KEYWORDS[data.pressureDomain] ?? [])
      : [];

    const scored = rows.map((row) => {
      let score = 0;
      if (row.isFoundingCoach) score += 5;
      else if (row.isFeaturedCoach) score += 3;

      // Bonus point for domain relevance
      const category = (row.mainCoachingCategory ?? "").toLowerCase();
      if (domainKeywords.some((kw) => category.includes(kw))) score += 1;

      // Jitter so results vary between requests
      score += Math.random() * 0.6;

      return { row, score };
    });

    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, data.limit)
      .map(({ row }) => ({
        username: row.username,
        firstName: row.firstName,
        lastName: row.lastName,
        coachingTitle: row.coachingTitle ?? null,
        profilePhotoUrl: row.profilePhotoUrl ?? null,
        isFoundingCoach: row.isFoundingCoach ?? false,
        isFeaturedCoach: row.isFeaturedCoach ?? false,
        membershipPlan: row.membershipPlan ?? MembershipPlan.Standard,
      }));
  });
